import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError, setToken, setRefreshToken, getToken, decodeJwt, refreshAccessToken } from "./api";

const AuthContext = createContext(null);
const REFRESH_LEAD_MS = 5 * 60 * 1000; // rinnovo 5 minuti prima della scadenza
const MIN_REFRESH_MS = 10 * 1000; // minimo 10s se token quasi scaduto

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const refreshTimer = useRef(null);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
    const tok = getToken();
    if (!tok) return;
    const payload = decodeJwt(tok);
    if (!payload?.exp) return;
    const nowMs = Date.now();
    const expMs = payload.exp * 1000;
    const delay = Math.max(expMs - nowMs - REFRESH_LEAD_MS, MIN_REFRESH_MS);
    // Se già scaduto o quasi, refresh subito
    if (expMs - nowMs < REFRESH_LEAD_MS) {
      doRefresh();
      return;
    }
    refreshTimer.current = setTimeout(doRefresh, delay);
  }, []);

  const doRefresh = useCallback(async () => {
    try {
      await refreshAccessToken();
      scheduleRefresh();
    } catch (e) {
      // Refresh fallito → forzo logout locale
      setToken(null);
      setRefreshToken(null);
      setUser(false);
    }
  }, [scheduleRefresh]);

  const fetchMe = useCallback(async () => {
    // Se non ho nessun token in locale, salto la chiamata (l'interceptor gestisce comunque il 401)
    if (!getToken()) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      scheduleRefresh();
    } catch (e) {
      // L'interceptor ha già tentato il refresh; se qui arriva 401 la sessione è persa
      setToken(null);
      setRefreshToken(null);
      setUser(false);
    }
  }, [scheduleRefresh]);

  useEffect(() => {
    fetchMe();
    const onAuthExpired = (e) => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      const reason = e?.detail?.reason;
      if (reason === "session_terminated") {
        try {
          toast.error(
            "Sessione terminata: è stato effettuato l'accesso da un altro dispositivo. Se non sei stato tu, cambia subito la password.",
            { duration: 8000 },
          );
        } catch {}
      }
      setUser(false);
    };
    window.addEventListener("adybo:auth-expired", onAuthExpired);
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      window.removeEventListener("adybo:auth-expired", onAuthExpired);
    };
  }, [fetchMe]);

  const login = async (email, password) => {
    setError("");
    setToken(null);
    setRefreshToken(null);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) setToken(data.access_token);
      if (data.refresh_token) setRefreshToken(data.refresh_token);
      setUser(data.user);
      scheduleRefresh();
      return { ok: true };
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      return { ok: false, error: msg };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    setToken(null);
    setRefreshToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, error, login, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
